// src/components/MapWithDrawing/DrawingList.js
import React, { useMemo } from 'react';
import {
    List, ListItem, ListItemText, IconButton, Typography, Button,
    Box, Chip, Tooltip, Badge
} from '@material-ui/core';
import { 
    Delete, 
    Edit as EditIcon,
    Save as SaveIcon,
    FiberNew as NewIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    listItem: {
        borderLeft: '3px solid transparent',
        marginBottom: '4px',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }
    },
    listItemActive: {
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    listHeader: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    itemType: {
        fontSize: '0.7rem',
        padding: '2px 6px',
        height: '18px',
        marginLeft: theme.spacing(1),
    },
    actionButtons: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
    },
    listContainer: {
        maxHeight: '250px',
        overflow: 'auto',
    },
    colorSwatch: {
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        display: 'inline-block',
        marginRight: theme.spacing(1),
        border: '1px solid rgba(0,0,0,0.1)',
    },
    newIndicator: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontSize: '0.65rem',
        padding: '2px 4px',
        borderRadius: '4px',
        marginLeft: theme.spacing(1),
        textTransform: 'uppercase',
        fontWeight: 'bold',
    },
    badgeRoot: {
        marginRight: theme.spacing(1),
    },
    badge: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
    }
}));

// Helper function to render item type chip
const ItemTypeChip = ({ type }) => {
    let label, color;
    
    switch (type) {
        case 'polygon':
        case 'area':
            label = 'Area';
            color = 'primary';
            break;
        case 'subtraction':
            label = 'Exclusion';
            color = 'secondary';
            break;
        case 'geofence':
        case 'circle':
            label = 'Geofence';
            color = 'default';
            break;
        case 'propertyLine':
        case 'manualPropertyLine':
            label = 'Property Line';
            color = 'default';
            break;
        default:
            label = type || 'Unknown';
            color = 'default';
    }
    
    return <Chip size="small" label={label} color={color} style={{fontSize: '0.7rem', height: '20px'}} />;
};

// Sort function to organize items by type and status
const sortItems = (a, b) => {
    // First group by type
    const typeOrder = {
        'area': 1,
        'polygon': 1,
        'geofence': 2,
        'circle': 2,
        'subtraction': 3,
        'propertyLine': 4,
        'manualPropertyLine': 4
    };
    
    const aTypeOrder = typeOrder[a.type] || 99;
    const bTypeOrder = typeOrder[b.type] || 99;
    
    if (aTypeOrder !== bTypeOrder) {
        return aTypeOrder - bTypeOrder;
    }
    
    // Then sort by whether they're new or existing (new comes first)
    const aIsNew = a.isNew || false;
    const bIsNew = b.isNew || false;
    
    if (aIsNew !== bIsNew) {
        return aIsNew ? -1 : 1;
    }
    
    // Finally sort by name
    return (a.name || '').localeCompare(b.name || '');
};

// Memoize this component
const DrawingList = React.memo(({
    drawings = [],
    polygons = [],
    selectedDrawingId,
    onSelectDrawing,
    onDeleteDrawing,
    onClearAll,
    onSaveAll,
    isDrawingMode,
    listStyle // Pass list style class via prop
}) => {
    const classes = useStyles();
    
    // Combine and format all items for display
    const allItems = useMemo(() => {
        // Format drawings, marking them as "new"
        const formattedDrawings = drawings.map(drawing => ({
            ...drawing,
            isNew: true,
            displayType: drawing.type || 'polygon',
            fillColor: drawing.style?.fillColor || drawing.fillColor || '#3388ff'
        }));
        
        // Format polygons, marking them as existing
        const formattedPolygons = polygons
            // Filter out property lines unless they're editable
            .filter(p => (
                p.type !== 'propertyLine' || 
                p.type === 'manualPropertyLine' || 
                p.type === 'temporaryPropertyLine'
            ))
            .map(polygon => ({
                ...polygon,
                isNew: false,
                displayType: polygon.type || 'area',
                fillColor: polygon.fillColor || '#3388ff'
            }));
        
        // Combine and sort
        return [...formattedDrawings, ...formattedPolygons].sort(sortItems);
    }, [drawings, polygons]);
    
    // Count of new items
    const newItemsCount = drawings.length;
    
    return (
        <>
            <Typography variant="subtitle1" gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
                Map Areas Management  
                {allItems?.length > 0 && (
                   <Chip size="small" label={`${allItems.length} Areas`} style={{ marginLeft: 15, backgroundColor: '#accacd', height: '30px'}} />
                )}
            </Typography>
            
            <div className={classes.listContainer}>
                <List dense className={listStyle}>
                    {allItems.length === 0 ? (
                        <ListItem><ListItemText primary="No areas available. Start drawing to add new areas." /></ListItem>
                    ) : (
                        allItems.map((item) => {
                            return (
                                <ListItem
                                    key={`item-${item.id}`}
                                    button
                                    selected={selectedDrawingId === item.id}
                                    onClick={() => onSelectDrawing(item.id)}
                                    className={selectedDrawingId === item.id ? classes.listItemActive : classes.listItem}
                                    disabled={isDrawingMode}
                                >
                                    <div className={classes.colorSwatch} style={{backgroundColor: item.fillColor}} />
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center">
                                                <span style={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '120px'
                                                }}>
                                                    {item.name || `Unnamed ${item.displayType}`}
                                                </span>
                                                {item.isNew && (
                                                    <span className={classes.newIndicator}>new</span>
                                                )}
                                            </Box>
                                        }
                                        primaryTypographyProps={{ 
                                            noWrap: true
                                        }}
                                        secondary={
                                            <>
                                                <ItemTypeChip type={item.displayType} />
                                                {!item.isNew && item.area && 
                                                    <Typography variant="caption" style={{marginLeft: '8px'}}>
                                                        {parseFloat(item.area).toFixed(1)} sq ft
                                                    </Typography>
                                                }
                                            </>
                                        }
                                    />
                                  <div>
                                        
                                 
                                        <Tooltip title="Edit">
                                            <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectDrawing(item.id);
                                                }}
                                                disabled={isDrawingMode}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Area">
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); onDeleteDrawing(item.id); }}
                                                disabled={isDrawingMode}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </ListItem>
                            );
                        })
                    )}
                </List>
            </div>
            
            <div className={classes.actionButtons}>
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={onClearAll}
                    disabled={(polygons.length === 0 && drawings.length === 0) || isDrawingMode}
                    startIcon={<Delete />}
                >
                    Clear All
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={onSaveAll}
                    disabled={(polygons.length === 0 && drawings.length === 0) || isDrawingMode}
                    startIcon={<SaveIcon />}
                >
                    Save All
                </Button>
            </div>
        </>
    );
});

export default DrawingList;